import { RECEIVE_LAYOUT_CONFIG, type ReceiveFieldRow, type SheetOrigin } from "@/excel/speckle/receiveTypes";

const RECEIVE_TABLE_COLORS = {
  primary: "#0B1F3A",
  onPrimary: "#FFFFFF",
  body: "#FFFFFF",
  zebra: "#F7F9FC",
  grid: "#D9D9D9",
  sectionBorder: "#9FB2C8",
  inputAccent: "#F5A400",
  text: "#1F2937",
  mutedText: "#5F6B7A",
} as const;

function applyBorder(
  range: Excel.Range,
  side: Excel.BorderIndex,
  style: Excel.BorderLineStyle,
  weight?: Excel.BorderWeight,
  color?: string
): void {
  const border = range.format.borders.getItem(side);

  border.style = style;

  if (weight) {
    border.weight = weight;
  }

  if (color) {
    border.color = color;
  }
}

function applyDefaultReceiveTableGrid(range: Excel.Range): void {
  const sides = [
    Excel.BorderIndex.edgeTop,
    Excel.BorderIndex.edgeBottom,
    Excel.BorderIndex.edgeLeft,
    Excel.BorderIndex.edgeRight,
    Excel.BorderIndex.insideHorizontal,
    Excel.BorderIndex.insideVertical,
  ];

  for (const side of sides) {
    applyBorder(range, side, Excel.BorderLineStyle.continuous, undefined, RECEIVE_TABLE_COLORS.grid);
  }
}

function applyReceiveHeaderFormat(range: Excel.Range): void {
  range.format.fill.color = RECEIVE_TABLE_COLORS.primary;
  range.format.font.color = RECEIVE_TABLE_COLORS.onPrimary;
  range.format.font.bold = true;
  range.format.horizontalAlignment = Excel.HorizontalAlignment.left;

  applyBorder(
    range,
    Excel.BorderIndex.edgeBottom,
    Excel.BorderLineStyle.continuous,
    Excel.BorderWeight.medium,
    RECEIVE_TABLE_COLORS.sectionBorder
  );
}

function applyEditableValueCellFormat(valueCell: Excel.Range): void {
  valueCell.format.font.bold = true;

  applyBorder(
    valueCell,
    Excel.BorderIndex.edgeLeft,
    Excel.BorderLineStyle.continuous,
    Excel.BorderWeight.medium,
    RECEIVE_TABLE_COLORS.inputAccent
  );
}

function applyReadOnlyValueCellFormat(valueCell: Excel.Range): void {
  valueCell.format.font.italic = true;
  valueCell.format.font.color = RECEIVE_TABLE_COLORS.mutedText;
}

function topGroupKey(path: string): string {
  return path.split(".").filter(Boolean)[0] ?? "";
}

function isNewSectionRow(current: ReceiveFieldRow, previous: ReceiveFieldRow | undefined): boolean {
  return current.rowId > 1 && topGroupKey(current.path) !== topGroupKey(previous?.path ?? "");
}

function applyReceiveBodyFormat(
  worksheet: Excel.Worksheet,
  tableOrigin: SheetOrigin,
  fieldRows: ReceiveFieldRow[]
): void {
  if (fieldRows.length === 0) {
    return;
  }

  const bodyRange = worksheet.getRangeByIndexes(
    tableOrigin.startRow + 1,
    tableOrigin.startColumn,
    fieldRows.length,
    RECEIVE_LAYOUT_CONFIG.columnCount
  );

  bodyRange.format.font.bold = false;
  bodyRange.format.font.italic = false;
  bodyRange.format.font.color = RECEIVE_TABLE_COLORS.text;
  bodyRange.format.horizontalAlignment = Excel.HorizontalAlignment.left;

  for (const fieldRow of fieldRows) {
    const rowRange = worksheet.getRangeByIndexes(
      tableOrigin.startRow + fieldRow.rowId,
      tableOrigin.startColumn,
      1,
      RECEIVE_LAYOUT_CONFIG.columnCount
    );
    const valueCell = worksheet.getRangeByIndexes(
      tableOrigin.startRow + fieldRow.rowId,
      tableOrigin.startColumn + RECEIVE_LAYOUT_CONFIG.valueColumnOffset,
      1,
      1
    );

    rowRange.format.fill.color = fieldRow.rowId % 2 === 0 ? RECEIVE_TABLE_COLORS.zebra : RECEIVE_TABLE_COLORS.body;

    if (isNewSectionRow(fieldRow, fieldRows[fieldRow.rowId - 2])) {
      applyBorder(
        rowRange,
        Excel.BorderIndex.edgeTop,
        Excel.BorderLineStyle.continuous,
        Excel.BorderWeight.medium,
        RECEIVE_TABLE_COLORS.sectionBorder
      );
    }

    if (fieldRow.editable) {
      applyEditableValueCellFormat(valueCell);
    } else {
      applyReadOnlyValueCellFormat(valueCell);
    }
  }
}

export function applyReceiveTableFormat(
  worksheet: Excel.Worksheet,
  tableOrigin: SheetOrigin,
  fieldRows: ReceiveFieldRow[]
): void {
  const tableRange = worksheet.getRangeByIndexes(
    tableOrigin.startRow,
    tableOrigin.startColumn,
    fieldRows.length + 1,
    RECEIVE_LAYOUT_CONFIG.columnCount
  );
  const headerRange = worksheet.getRangeByIndexes(
    tableOrigin.startRow,
    tableOrigin.startColumn,
    1,
    RECEIVE_LAYOUT_CONFIG.columnCount
  );

  applyDefaultReceiveTableGrid(tableRange);
  applyReceiveHeaderFormat(headerRange);
  applyReceiveBodyFormat(worksheet, tableOrigin, fieldRows);
}
